import type { PlanAccess } from '@/types/api'

/**
 * R10-08 — aviso de fim do acesso com oferta de upgrade.
 *
 * Le so o `plan_access` de `/auth/me`. O acesso acaba quando vence o ULTIMO plano ativo:
 * quem tem outro plano que continua depois nao e avisado. O plano que vence por ultimo
 * decide o texto (gratuito ou pago) e a oferta (`upgrade_checkout_path`, configurada no
 * plano pelo admin).
 */

/** Quantos dias antes do fim o aviso aparece. O texto do admin (UpgradePlanField) usa o mesmo. */
export const PLAN_EXPIRY_WARNING_DAYS = 5

export interface PlanExpiryNoticeData {
  planId: number
  planName: string
  free: boolean
  /** Dias de calendario ate o fim, no relogio do aluno: 0 = hoje, 1 = amanha. */
  daysLeft: number
  expiresAt: string
  upgradePath: string | null
  upgradePlanName: string | null
}

export function planExpiryNotice(access: PlanAccess | undefined, now: Date): PlanExpiryNoticeData | null {
  if (!access || access.full_access || access.plans.length === 0) return null
  // Plano sem validade nunca acaba: nada a avisar.
  if (access.plans.some((plan) => !plan.expires_at)) return null

  const last = access.plans.reduce((latest, plan) =>
    new Date(plan.expires_at as string) > new Date(latest.expires_at as string) ? plan : latest,
  )
  const expiresAt = last.expires_at as string
  const daysLeft = calendarDaysBetween(now, new Date(expiresAt))
  if (daysLeft < 0 || daysLeft > PLAN_EXPIRY_WARNING_DAYS) return null

  return {
    planId: last.id,
    planName: last.name,
    free: last.free ?? false,
    daysLeft,
    expiresAt,
    upgradePath: last.upgrade_checkout_path ?? null,
    upgradePlanName: last.upgrade_plan_name ?? null,
  }
}

export function planExpiryMessage(notice: Pick<PlanExpiryNoticeData, 'free' | 'planName' | 'daysLeft'>): string {
  const subject = notice.free ? 'Seu acesso gratuito' : `Seu plano ${notice.planName}`
  return `${subject} termina ${whenLabel(notice.daysLeft)}.`
}

function whenLabel(daysLeft: number): string {
  if (daysLeft === 0) return 'hoje'
  if (daysLeft === 1) return 'amanhã'
  return `em ${daysLeft} dias`
}

function calendarDaysBetween(from: Date, to: Date): number {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate())
  return Math.round((end.getTime() - start.getTime()) / 86_400_000)
}
